import React from 'react';
import { Box, Typography, Chip, Tooltip } from '@mui/material';

type Environment = 'development' | 'staging' | 'production' | 'local';

interface AppFooterProps {
  variant?: 'minimal' | 'full';
}

/**
 * Application footer component displaying version and environment information.
 * Works with both local development and ECS deployments.
 */
const AppFooter: React.FC<AppFooterProps> = ({ variant = 'minimal' }) => {
  // Get version from environment or package.json (injected at build time)
  const version = process.env.REACT_APP_VERSION || '0.1.0';
  
  // Detect environment - check multiple sources for flexibility
  const getEnvironment = (): Environment => {
    // First check explicit environment variable
    const envVar = process.env.REACT_APP_ENVIRONMENT?.toLowerCase();
    if (envVar && ['development', 'staging', 'production', 'local'].includes(envVar)) {
      return envVar as Environment;
    }
    
    // Check NODE_ENV
    if (process.env.NODE_ENV === 'development') {
      return 'local';
    }
    
    // Check hostname for common patterns
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local')) {
        return 'local';
      }
      if (hostname.includes('staging') || hostname.includes('stage') || hostname.includes('dev')) {
        return 'staging';
      }
    }
    
    // Default to production for deployed environments
    return 'production';
  };

  const environment = getEnvironment();
  const buildDate = process.env.REACT_APP_BUILD_DATE || '';
  const commitHash = process.env.REACT_APP_COMMIT_HASH || '';

  const getEnvColor = (env: Environment): 'success' | 'warning' | 'info' | 'default' => {
    switch (env) {
      case 'production':
        return 'success';
      case 'staging':
        return 'warning';
      case 'development':
      case 'local':
      default:
        return 'info';
    }
  };

  const getEnvLabel = (env: Environment): string => {
    switch (env) {
      case 'local':
        return 'LOCAL';
      case 'development':
        return 'DEV';
      case 'staging':
        return 'STAGING';
      case 'production':
        return 'PROD';
    }
  };

  const formatBuildDate = (dateStr: string): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const buildInfo = [
    version && `v${version}`,
    commitHash && commitHash.substring(0, 7),
    buildDate && formatBuildDate(buildDate),
  ]
    .filter(Boolean)
    .join(' • ');

  return (
    <Box
      component="footer"
      sx={{
        py: 1.5,
        px: 3,
        mt: 4,
        backgroundColor: 'grey.50',
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {/* Left side: Copyright */}
        <Typography variant="caption" color="text.secondary">
          © {new Date().getFullYear()} AJ Group Holdings
        </Typography>

        {/* Right side: Version & Environment */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {variant === 'full' && buildDate && (
            <Typography variant="caption" color="text.disabled">
              Built: {formatBuildDate(buildDate)}
            </Typography>
          )}

          {variant === 'full' && commitHash && (
            <Tooltip title={`Commit: ${commitHash}`}>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontFamily: 'monospace', cursor: 'help' }}
              >
                {commitHash.substring(0, 7)}
              </Typography>
            </Tooltip>
          )}

          <Tooltip title={buildInfo}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: 'monospace', cursor: 'help' }}
            >
              v{version}
            </Typography>
          </Tooltip>

          <Chip
            label={getEnvLabel(environment)}
            size="small"
            color={getEnvColor(environment)}
            variant={environment === 'production' ? 'filled' : 'outlined'}
            sx={{
              height: 18,
              fontSize: '0.625rem',
              fontWeight: 600,
              '& .MuiChip-label': {
                px: 1,
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AppFooter;

